/*
 * One screen in free layout (docs/hmi-panel-ausbau.md §2): every widget at
 * its px position on the design surface, stacked by z, the whole surface
 * scaled uniformly to the space available.
 *
 * `fit`:
 *  - 'contain': the whole surface is visible, centred (panel, gateway kiosk);
 *  - 'width': as wide as the space, height follows (cloud page, scrolls).
 */

import type { ComponentChildren } from 'preact';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { ScreenLayout, WidgetDefinition, WidgetPlacement } from '../widgets';
import { WidgetHost } from '../WidgetHost';
import { FRAME_SIZES } from '../editor/editorModel';
import { isHexColor } from '../themeModel';
import { useHmiRuntime } from './runtimeContext';
import './stage.css';

/** Kinds that are drawing elements, not tiles: in a free layout they stand
 *  on the background without a card unless their look asks for one. */
export const BARE_KINDS: ReadonlySet<string> = new Set(['shape', 'symbol', 'text', 'image', 'nav-bar', 'action-button', 'theme-toggle', 'user-invite']);

export function frameOf(layout: ScreenLayout): { width: number; height: number } {
  return layout.frame ?? FRAME_SIZES[layout.target] ?? { width: 1280, height: 800 };
}

export function freeOrder(ids: readonly string[], placements: Record<string, WidgetPlacement>): string[] {
  return ids
    .filter((id) => placements[id])
    .map((id, i) => ({ id, i, z: placements[id]!.z ?? 0 }))
    .sort((a, b) => a.z - b.z || a.i - b.i)
    .map((x) => x.id);
}

interface Props {
  layout: ScreenLayout;
  widgetIds: readonly string[];
  widgetsById: Map<string, WidgetDefinition>;
  fit: 'contain' | 'width';
  /** Rendered on top of each item (editor handles). */
  decorate?: (widgetId: string) => ComponentChildren;
  /** Reports the scale in use (editor converts pointer moves with it). */
  onScale?: (scale: number) => void;
}

export function FreeStage({ layout, widgetIds, widgetsById, fit, decorate, onScale }: Props) {
  const host = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const frame = frameOf(layout);
  const { assetUrl } = useHmiRuntime();

  useLayoutEffect(() => {
    const el = host.current;
    if (!el) return;
    const measure = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scale = box.w === 0 ? 0 : fit === 'width'
    ? box.w / frame.width
    : Math.min(box.w / frame.width, box.h / frame.height);
  useLayoutEffect(() => { if (scale > 0) onScale?.(scale); }, [scale, onScale]);

  const bg = layout.background;
  const surfaceStyle: Record<string, string> = {
    width: `${frame.width}px`,
    height: `${frame.height}px`,
    transform: `scale(${scale})`,
  };
  if (isHexColor(bg?.color)) surfaceStyle.backgroundColor = bg!.color!;
  if (bg?.assetRef) surfaceStyle.backgroundImage = `url("${assetUrl(bg.assetRef).replace(/["\\]/g, '')}")`;

  return (
    <div ref={host} class={`hv-stage hv-stage--${fit}`}
         style={fit === 'width' ? { height: `${frame.height * scale}px` } : undefined}>
      <div class="hv-stage__surface" style={surfaceStyle}>
        {freeOrder(widgetIds, layout.placements).map((id) => {
          const widget = widgetsById.get(id);
          const p = layout.placements[id]!;
          if (!widget) return null;
          return (
            <div key={id} class={`hv-free-item hv-hmi-cell${BARE_KINDS.has(widget.kind) ? ' hv-free-item--bare' : ''}`}
                 data-widget-id={id}
                 style={{
                   left: `${p.x ?? 0}px`, top: `${p.y ?? 0}px`,
                   width: `${p.w ?? 100}px`, height: `${p.h ?? 100}px`, zIndex: String(p.z ?? 0),
                 }}>
              <WidgetHost widget={widget} />
              {decorate?.(id)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
